import * as Tone from "tone";

// short fade at the choke point so cutting a slice mid-sound never clicks
const CHOKE_FADE = 0.005

type Hit = {
    buffer: Tone.ToneAudioBuffer;
    output: Tone.ToneAudioNode;
    time: number;      // real time
    offset: number;    // buffer time
    duration: number;  // buffer time — occupies duration / rate real seconds
    rate: number;      // varispeed — sets the real-time grid (durations, retrig spacing)
    pitchRate: number; // extra playback-rate multiplier — changes pitch only, the
                       // hit fills the same window with pitchRate× the material
    retrig: number;
}

// The engine is monophonic: starting any hit — step or retrig repeat, whatever
// the player type — chokes whatever is still sounding, so slices can never
// smear into each other.
const createMonoVoice = () => {
    let last: { source: Tone.ToneBufferSource; end: number } | null = null

    const hit = ({buffer, output, time, offset, duration, rate, pitchRate}: Omit<Hit, 'retrig'>) => {
        // stop() past a source's scheduled end would cancelStop() and extend it
        // instead, so only choke genuine overlaps
        if (last !== null && !last.source.disposed && time < last.end - 0.001) {
            last.source.stop(time)
        }

        const source = new Tone.ToneBufferSource({
            url: buffer,
            playbackRate: rate * pitchRate,
            fadeOut: CHOKE_FADE,
        }).connect(output)
        // Tone.Offline steps the whole event timeline before the native render,
        // so an onended dispose there disconnects every source pre-render and
        // the export comes out silent. Offline contexts are torn down wholesale
        // after rendering anyway — only live playback needs per-hit cleanup.
        if (!source.context.isOffline) {
            source.onended = () => source.dispose()
        }

        const realDuration = duration / rate
        source.start(time, offset, realDuration)
        last = {source, end: time + realDuration}
    }

    return ({retrig, ...hitValues}: Hit) => {
        if (retrig > 0) {
            // tile the step with even repeats of the slice head, each cut to
            // its 1/retrig share so repeats can never overlap each other
            const subDuration = hitValues.duration / retrig
            for (let i = 0; i < retrig; i++) {
                hit({
                    ...hitValues,
                    time: hitValues.time + (i * subDuration) / hitValues.rate,
                    duration: subDuration,
                })
            }
            return
        }
        hit(hitValues)
    }
}

export {createMonoVoice}
